import draggable from "vuedraggable";
import firebase from "firebase/app";
import { ValidationObserver, ValidationProvider } from "vee-validate";
import {EventLocations} from "@/enums/EventLocations";
import {DigitalSignagesTypes} from "@/enums/DigitalSignagesTypes";
import ManageDevicesDlg from "@/views/digital-signage/manage-devices-dlg/ManageDevicesDlg.vue";
import {DigitalSignageDevice} from "@/models/digital-signage/DigitalSignageDevice";
import { DigitalSignageDateOptions } from "@/enums/DigitalSignageDateOptions";
import AccessControlMixin from "@/mixins/AccessControlMixin";
import moment from "moment";

export default {
    name: "DigitalSignageList",
    mixins: [AccessControlMixin],
    components: {
        draggable,
        ValidationObserver,
        ValidationProvider,
        ManageDevicesDlg
    },
    filters: {
        overflowEllipsis(value) {
            if (value.length > 30) {
                return value.slice(0, 30) + "..."
            } else {
                return value;
            }
        }
    },
    computed: {
        digitalSignages() {
            return this.$store.state.digitalSignageModule.currentDigitalSignageList
        },
        digitalSignageDevices(): DigitalSignageDevice[] {
            return this.$store.state.digitalSignageModule.digitalSignageDevices;
        },
        dragOptions() {
            return {
                animation: 0,
                disabled: false,
                ghostClass: "ghost"
            };
        },
        eventLocationsWings() {
            return this.$store.state.eventLocationModule.eventLocations.filter(x => x.type === EventLocations.Wing)
        },
        digitalSignageTypes() {
            return DigitalSignagesTypes;
        },
        digitalSignageDateOptions() {
            return DigitalSignageDateOptions;
        },
        facility() {
            return this.$store.state.facilityModule.selectedFacility;
        },
        isStaffHubDevicesColumnDigitalSignage() {
            return this.facility?.isStaffHubDevicesColumnDigitalSignage;
        }
    },
    async created() {
        Promise.all([
            this.$store.dispatch("getDigitalSignages"),
            this.$store.dispatch("getDigitalSignageDevices"),
            this.$store.dispatch("getEventLocations"),
        ]).then(()=>{
            this.listLoading = false
        })
    },
    data() {
        return {
            search: "",
            dialog : null,
            newName: '',
            newAudience: '',
            listLoading: true,
            newIsFamilyChannel: false,
            newIsResidentChannel: false,
            headers: [
                {
                    text: "Name",
                    align: "start",
                    value: "channelName"
                }
            ],
            digitalSignagesGrouped: [],
            loading: false
        };
    },
    mounted() {
        document.addEventListener('click', event => {
            if (!(event.target instanceof Element)) return;

            const isOutsideClick = !event.target.closest('.underline');

            if (isOutsideClick) {
                document.querySelectorAll('.tooltip-ds').forEach(el => el.classList.remove('active'));
            }
        });
    },
    methods: {
        handleClickDigitalSignages(row) {
            this.$router.push("/digital-signage/" + row.id.toString())
        },
        createDigitalSignageChannel(){
            const dsChannel = {
                channelName: this.newName,
                isFamilyChannel: this.newIsFamilyChannel,
                isResidentChannel : this.newIsResidentChannel,
                isEnabled: true,
                orderIndex: this.generateIndex(),
                selectedWings: this.eventLocationsWings.map(x => x.id),
                isShowScreensaver: true,
                pages: [ {
                    type: this.digitalSignageTypes.ContentCreator,
                    name: "My First Page",
                    content: "",
                    enabled: true,
                    timeInSeconds: 10,
                    url: '',
                    hasExpirationDate: false,
                    displayDate: false,
                    displayOn: this.digitalSignageDateOptions.AllTheTime,
                    displayForDays: [],
                    startDate: moment(new Date()).format("YYYY-MM-DD"),
                    time: {
                        start: moment("09:00", "HH:mm").format(this.facility.timeFormat),
                        end: moment("17:00", "HH:mm").format(this.facility.timeFormat)
                    }
                }]
            }
            this.$store.dispatch("createDigitalSignage", dsChannel);
        },
        sortByType(list) {
            return list
                .filter(item => item.channelName && item.channelName.toLowerCase().includes(this.search.toLowerCase()))
                .sort((a, b) => {
                    if (a.isEnabled !== b.isEnabled) {
                        return b.isEnabled - a.isEnabled;
                    } else {
                        return a.orderIndex - b.orderIndex;
                    }
                });
        },
        onSignageSearch(){
            if(this.search.length){
                this.dragOptions.disabled = true
                this.digitalSignagesGrouped = this.sortByType(this.digitalSignages)
            }
            else{
                this.digitalSignagesGrouped = this.sortByType(this.digitalSignages)
                this.dragOptions.disabled = false
            }
        },
        generateIndex() {
            const arr = this.digitalSignagesGrouped.filter(x => x.isEnabled === true).map(x => Number(x.orderIndex))
            let index = Math.max(...arr) + 1;

            if(!arr.length) {
                index = 1;
            }

            return index;
        },
        async changeEnableSignage(item) {
            if(!item.isEnabled) {
                const index = this.digitalSignagesGrouped.findIndex(x => x.id === item.id);
                this.digitalSignagesGrouped[index].orderIndex = null;

                const result = this.digitalSignagesGrouped.filter(x => x.id !== item.id);
                this.digitalSignagesGrouped = [...result, item];
            }

            await this.updateDigitalSignage();
        },
        async updateDigitalSignage() {
            this.loading = true;

            let count = 1;
            this.digitalSignagesGrouped.forEach(item => {
                if(item.isEnabled) {
                    item.orderIndex = count++;
                }
            });
            count = 1;

            for (const signage of this.digitalSignagesGrouped) {
                await firebase
                  .firestore()
                  .collection("digitalSignage")
                  .doc(signage.id)
                  .update({
                      "isEnabled": signage.isEnabled,
                      "orderIndex": signage.orderIndex
                  });
            }

            this.loading = false;
        },
        close() {
            this.dialog = false;
            this.newName = "";
            this.newIsResidentChannel = false;
            this.newIsFamilyChannel = false;
            this.$refs.observer.reset();
        },
        wing(item) {
            if(Object.prototype.hasOwnProperty.call(item, 'selectedWings')) {
                if(this.eventLocationsWings.length === item.selectedWings.length) {
                    return this.$t('GeneralTranslations.All')
                }

                let str = "";

                this.eventLocationsWings.forEach(x => {
                    if(item?.selectedWings.length && item.selectedWings.includes(x.id) && x.name.length) {
                        str = str.length ? `${str}, ${x.name}` : `${x.name}`
                    }
                });

                return str;
            } else {
                return this.$t('GeneralTranslations.All');
            }
        },
        devices(item) {
            const now: number = Math.floor(new Date().getTime() / 1000);
            const ACTIVE_THRESHOLD = 15 * 60;

            const devices = this.digitalSignageDevices
              .filter(x => x.digitalSignageId === item.id)
              .filter(x => moment(x.lastCheckin.seconds * 1000).toDate() > moment(new Date()).subtract('days', 30).toDate())
              .map(x => {
                  const lastCheckin: number = x.lastCheckin.seconds;
                  const isActive = (now - lastCheckin) <= ACTIVE_THRESHOLD && item.isOnline;
                  const deviceName = x?.deviceNameEdited ?? x?.deviceName;

                  return {
                      html: `<span class="${isActive ? 'green--text' : 'red--text'}">${deviceName}</span>`,
                      text: deviceName
                  };
              });

            return {
                html: devices.map(d => d.html).join(', '),
                text: devices.map(d => d.text).join(', ')
            };
        },
        toggleTooltip(event: MouseEvent) {
            if (!(event.target instanceof Element)) return;
            const paragraph = event.target.closest('.underline');
            if (!paragraph) return;

            const tooltip = paragraph.nextElementSibling;
            const isActive = tooltip.classList.contains('active');
            document.querySelectorAll('.tooltip-ds').forEach(el => el.classList.remove('active'));
            if (!isActive) {
                tooltip.classList.add('active');
            }
        },
        closeTooltip() {
            document.querySelectorAll('.tooltip-ds').forEach(el => el.classList.remove('active'));
        },
        openManageDevicesDialog() {
            this.$refs.manageDevicesDlg.open();
        }
    },
    watch: {
        digitalSignages(list) {
            if(list) {
                this.digitalSignagesGrouped = this.sortByType(list);
            }
        }
    }
};